import React, { useEffect, useState, Fragment } from "react";
import { useDispatch } from "react-redux";
import { Row, Col, Card, Button, Table } from "react-bootstrap";
import { Fn_FillListData, Fn_GetReport } from "../../store/Functions";
import { API_WEB_URLS } from "../../constants/constAPI";
import PageTitle from "../layouts/PageTitle";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { filterActiveMasters } from "../utils/masterFilters";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const currentYear = new Date().getFullYear();
const YEAR_LIST = Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);

const WagesSlipIndividual = () => {
  const dispatch = useDispatch();

  // Setup session state from localStorage
  const readSession = () => ({
    firm: (() => {
      try {
        return JSON.parse(localStorage.getItem("selectedFirm") || "null");
      } catch {
        return null;
      }
    })(),
    month: localStorage.getItem("selectedPayrollMonth") || "",
    year: parseInt(localStorage.getItem("selectedPayrollYear") || "0") || currentYear,
  });

  const [sessionFirm, setSessionFirm] = useState(() => readSession().firm);
  const [selectedMonth, setSelectedMonth] = useState(() => readSession().month);
  const [selectedYear, setSelectedYear] = useState(() => readSession().year);

  // Lists & loader states
  const [firmList, setFirmList] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [deptList, setDeptList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDept, setSelectedDept] = useState("");

  // Sync session changes from storage and sessionChange events
  useEffect(() => {
    const onStorage = () => {
      const s = readSession();
      setSessionFirm(s.firm);
      setSelectedMonth(s.month);
      setSelectedYear(s.year);
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener("sessionChange", onStorage);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("sessionChange", onStorage);
    };
  }, []);

  // Fetch initial masters
  useEffect(() => {
    const firmApiUrl = `${API_WEB_URLS.MASTER}/0/token/${API_WEB_URLS.FirmMaster}/Id/0`;
    Fn_FillListData(dispatch, setFirmList, "gridData", firmApiUrl);
    Fn_FillListData(dispatch, setDeptList, "gridData", `${API_WEB_URLS.MASTER}/0/token/DepartmentMaster/Id/0`);
  }, [dispatch]);

  // Load report data when inputs change
  useEffect(() => {
    const firmId = sessionFirm?.Id || 0;
    if (firmId && selectedMonth && selectedYear) {
      loadReportData(firmId, selectedMonth, selectedYear, selectedDept, searchTerm);
    } else {
      setReportData(null);
    }
  }, [sessionFirm, selectedMonth, selectedYear, selectedDept, searchTerm]);

  const loadReportData = async (firmId, monthName, year, departmentId, searchQuery) => {
    setIsLoading(true);
    try {
      if (monthName && year) {
        const fd = new FormData();
        fd.append("F_FirmMaster", firmId);
        fd.append("PayrollMonth", monthName);
        fd.append("PayrollYear", year);
        fd.append("F_DepartmentMaster", departmentId || 0);
        fd.append("EmployeeSearch", searchQuery || "");

        // Fetching Wages Slip data (shares the same backend stored proc as WagesSlipReport)
        const records = await Fn_GetReport(
          dispatch,
          () => {},
          "data",
          "Getwagesslip/0/token",
          { arguList: { id: 0, formData: fd } },
          true
        );

        let parsedData = null;
        if (records && records.length > 0) {
          const firstKey = Object.keys(records[0])[0];
          if (firstKey !== undefined && firstKey !== null) {
            const jsonString = records.map(row => row[firstKey] || "").join("");
            if (jsonString) {
              const rawObj = JSON.parse(jsonString);
              parsedData = rawObj.data || rawObj;
            }
          }
        }
        setReportData(parsedData);
      } else {
        setReportData(null);
      }
    } catch (err) {
      console.error("Error loading report data:", err);
      setReportData(null);
    } finally {
      setIsLoading(false);
    }
  };

  const getFirmPfNo = () => {
    if (reportData?.FirmDetails?.PFNo) return reportData.FirmDetails.PFNo;
    if (!sessionFirm || !sessionFirm.BranchData) return "N/A";
    try {
      const branches = JSON.parse(sessionFirm.BranchData);
      if (Array.isArray(branches) && branches.length > 0) {
        const mainBranch = branches.find(b => b.IsMainBranch === 1) || branches[0];
        return mainBranch.PFNo || "N/A";
      }
    } catch (e) {}
    return "N/A";
  };

  const getFirmEsiNo = () => {
    if (reportData?.FirmDetails?.ESINo) return reportData.FirmDetails.ESINo;
    if (!sessionFirm || !sessionFirm.BranchData) return "N/A";
    try {
      const branches = JSON.parse(sessionFirm.BranchData);
      if (Array.isArray(branches) && branches.length > 0) {
        const mainBranch = branches.find(b => b.IsMainBranch === 1) || branches[0];
        return mainBranch.ESINo || "N/A";
      }
    } catch (e) {}
    return "N/A";
  };

  const getFirmAddress = () => {
    if (reportData?.FirmDetails?.OfficeAddress) return reportData.FirmDetails.OfficeAddress;
    if (!sessionFirm || !sessionFirm.BranchData) return "N/A";
    try {
      const branches = JSON.parse(sessionFirm.BranchData);
      if (Array.isArray(branches) && branches.length > 0) {
        const mainBranch = branches.find(b => b.IsMainBranch === 1) || branches[0];
        return mainBranch.OfficeAddress || mainBranch.CoAddress || "N/A";
      }
    } catch (e) {}
    return "N/A";
  };

  const getSlips = () => {
    if (!reportData || !reportData.Records) return [];
    const query = searchTerm.toLowerCase();

    return reportData.Records.map((rec, index) => {
      const matchSearch = (rec.EmployeeName || rec.Name)?.toLowerCase().includes(query) || 
                          (rec.FatherName)?.toLowerCase().includes(query) || 
                          (rec.SerialNo || rec.AcNo || rec.EmpNo || String(rec.Sno))?.toLowerCase().includes(query);
      if (!matchSearch) return null;

      return {
        sno: index + 1,
        empNo: rec.EmpNo || rec.SerialNo || "N/A",
        name: (rec.EmployeeName || rec.Name || "").toUpperCase(),
        fatherName: (rec.FatherName || "").toUpperCase(),
        department: rec.DepartmentName || "STAFF",
        designation: rec.Designation || "STAFF",
        joinDate: rec.JoinDate ? new Date(rec.JoinDate).toLocaleDateString('en-GB') : "N/A",
        pfNo: rec.PFNo || "N/A",
        uan: rec.UAN || "N/A",
        esiNo: rec.ESINo || "N/A",
        bankName: rec.BankName || "N/A",
        bankAcNo: rec.BankAccountNo || "N/A",
        // Attendance
        wd: parseFloat(rec.WD || rec.WorkedDay || 0),
        wh: parseFloat(rec.WH || rec.WeeklyHoliday || 0),
        lw: parseFloat(rec.LW || rec.Absent || 0),
        fh: parseFloat(rec.FH || rec.FestiveHoliday || 0),
        cl: parseFloat(rec.CL || 0),
        ml: parseFloat(rec.ML || 0),
        pd: parseFloat(rec.PaidDays || rec.PaidDay || 0),
        // Standard Structure
        basicRate: parseFloat(rec.BasicRate || 0),
        hraRate: parseFloat(rec.HRARate || 0),
        conRate: parseFloat(rec.CONRate || 0),
        totalRate: parseFloat(rec.BasicRate || 0) + parseFloat(rec.HRARate || 0) + parseFloat(rec.CONRate || 0),
        // Earnings
        earnedBasic: parseFloat(rec.BasicEarned || rec.Wages || 0),
        earnedHRA: parseFloat(rec.HRAEarned || 0),
        earnedConveyance: parseFloat(rec.CONEarned || 0),
        arrear: parseFloat(rec.Arrear || 0),
        bonus: parseFloat(rec.BonusMonthly || rec.Bonus || 0),
        otAmount: parseFloat(rec.OTAmount || 0),
        otHours: parseFloat(rec.OTHours || 0),
        grossWages: parseFloat(rec.GrossWages || rec.GrossAmount || 0),
        // Deductions
        tds: parseFloat(rec.TDSDeduction || rec.TDS || 0),
        recovery: parseFloat(rec.Recovery || 0),
        esi: parseFloat(rec.ESIDeduction || rec.ESI || 0),
        emplrEsi: parseFloat(rec.EmplrESI || 0),
        pf: parseFloat(rec.PFDeduction || rec.PF || 0),
        epf: parseFloat(rec.EPF || 0),
        pension: parseFloat(rec.PensionDeduction || rec.Pension || 0),
        totalDed: parseFloat(rec.TotalDed || rec.TotalDeduction || 0),
        netPay: parseFloat(rec.NetPay || rec.NetPayment || 0)
      };
    }).filter(Boolean);
  };

  const slips = getSlips();

  // jsPDF export matching WagesSlip_Individual.pdf exactly (1 full slip per page)
  const exportToPDF = () => {
    if (slips.length === 0) {
      alert("No data available.");
      return;
    }

    const pdf = new jsPDF("p", "mm", "a4");

    slips.forEach((slip, index) => {
      if (index > 0) pdf.addPage();

      // Top Title Block
      pdf.setFontSize(14);
      pdf.setFont("Helvetica", "bold");
      pdf.text(sessionFirm?.Name?.toUpperCase() || "", 105, 15, { align: "center" });

      pdf.setFontSize(9);
      pdf.setFont("Helvetica", "normal");
      pdf.text(getFirmAddress(), 105, 20, { align: "center" });

      pdf.setFont("Helvetica", "bold");
      pdf.setFontSize(10);
      pdf.text(`WAGES/SALARY SLIP FOR THE MONTH OF ${(selectedMonth || "").toUpperCase()} ${selectedYear}`, 105, 28, { align: "center" });

      // Employee Grid details
      pdf.setFontSize(8.5);
      pdf.text(`Emp No: ${slip.empNo}`, 14, 38);
      pdf.text(`NAME: ${slip.name}`, 60, 38);
      pdf.text(`FATHER/HUSBAND'S: ${slip.fatherName}`, 130, 38);

      pdf.text(`Department: ${slip.department}`, 14, 44);
      pdf.text(`DESIGNATION: ${slip.designation}`, 130, 44);

      pdf.text(`Join Date: ${slip.joinDate}`, 14, 50);
      pdf.text(`PF No / UAN: ${getFirmPfNo()} / ${slip.uan}`, 14, 56);
      pdf.text(`E.S.I. No.: ${slip.esiNo}`, 130, 56);

      pdf.text(`Bank Name: ${slip.bankName}`, 14, 62);
      pdf.text(`Bank A/c No.: ${slip.bankAcNo}`, 130, 62);

      // Core Wages & Days Table Grid conformed to individual slip
      autoTable(pdf, {
        head: [["DAYS DETAIL", "SALARY STRUCTURE", "EARNINGS", "DEDUCTION"]],
        body: [
          [`WD: ${slip.wd.toFixed(2)}`, `BASIC: ${slip.basicRate.toFixed(2)}`, `Wages: ${slip.earnedBasic.toFixed(2)}`, `TDS: ${slip.tds.toFixed(2)}`],
          [`WH: ${slip.wh.toFixed(2)}`, "", `Arrear: ${slip.arrear.toFixed(2)}`, `Recovery: ${slip.recovery.toFixed(2)}`],
          [`LW: ${slip.lw.toFixed(2)}`, `HRA: ${slip.hraRate.toFixed(2)}`, `HRA: ${slip.earnedHRA.toFixed(2)}`, `ESI: ${slip.esi.toFixed(2)}`],
          [`FH: ${slip.fh.toFixed(2)}`, `CON: ${slip.conRate.toFixed(2)}`, `CON: ${slip.earnedConveyance.toFixed(2)}`, `Emplr. ESI: ${slip.emplrEsi.toFixed(2)}`],
          [`CL: ${slip.cl.toFixed(2)}`, "", `Adv. Bonus: ${slip.bonus.toFixed(2)}`, `PF: ${slip.pf.toFixed(2)}`],
          [`ML: ${slip.ml.toFixed(2)}`, "", `O T: ${slip.otAmount.toFixed(2)}`, `EPF: ${slip.epf.toFixed(2)}`],
          ["", "", "", `Pen: ${slip.pension.toFixed(2)}`],
          [`PD: ${slip.pd.toFixed(2)}`, `TOTAL: ${slip.totalRate.toFixed(2)}`, `PAID: ${slip.grossWages.toFixed(2)}`, ""],
          ["", "", "", ""],
          [`O T Hours: ${slip.otHours.toFixed(2)}`, "", `GROSS WAGES: ${slip.grossWages.toFixed(2)}`, `TOTAL DED: ${slip.totalDed.toFixed(2)}`],
          ["", "", "", `NET PAY: ${slip.netPay.toFixed(2)}`]
        ],
        startY: 68,
        margin: { left: 14, right: 14 },
        styles: { fontSize: 7.5, cellPadding: 2, lineColor: [0, 0, 0], lineWidth: 0.05 },
        headStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: "bold" },
        columnStyles: {
          0: { cellWidth: 45 },
          1: { cellWidth: 45 },
          2: { cellWidth: 45 },
          3: { cellWidth: 45 }
        }
      });

      const finalY = pdf.previousAutoTable.finalY || 160;

      // Signatures
      pdf.setFont("Helvetica", "bold");
      pdf.text("EMPLOYER'S SIGNATURE", 14, finalY + 12);
      pdf.text("EMPLOYEE'S SIGNATURE", 150, finalY + 12);
    });

    pdf.save(`WagesSlip_Individual_${selectedMonth}_${selectedYear}.pdf`);
  };

  const printStyles = `
    @media print {
      body * {
        visibility: hidden !important;
      }
      #print-wages-slips-individual, #print-wages-slips-individual * {
        visibility: visible !important;
      }
      #print-wages-slips-individual {
        display: block !important;
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        background: #fff;
        color: #000;
        font-family: Arial, sans-serif;
      }
      .no-print {
        display: none !important;
      }
      .print-individual-slip-page {
        page-break-after: always;
        width: 100%;
        padding: 20px;
        box-sizing: border-box;
      }
      .slip-grid-table {
        width: 100%;
        border-collapse: collapse;
        margin-top: 15px;
      }
      .slip-grid-table th, .slip-grid-table td {
        border: 1px solid #000;
        padding: 5px;
        font-size: 9px;
        text-align: left;
        vertical-align: top;
      }
      .slip-grid-table th {
        background: #f2f2f2;
        font-weight: bold;
      }
    }
  `;

  return (
    <Fragment>
      <style>{printStyles}</style>

      <div className="no-print">
        <PageTitle activeMenu="Wages Slip (Individual)" motherMenu="Reports" pageContent="Individual Payslips" />
      </div>

      {/* Filters Card */}
      <Card className="no-print mb-4 shadow-sm border-0" style={{ borderRadius: "10px", background: "#ffffff" }}>
        <Card.Body className="p-4">
          <Row className="align-items-end g-3">
            {/* Firm */}
            <Col lg={4} md={6}>
              <label className="form-label fw-semibold fs-13" style={{ color: "#495057" }}>
                Firm <span className="badge bg-light text-success ms-1 border" style={{ fontSize: "11px" }}>🔒 Header Locked</span>
              </label>
              <input
                type="text"
                className="form-control"
                style={{ height: "38px", borderRadius: "6px", fontSize: "13px", backgroundColor: "#f8f9fa", cursor: "not-allowed" }}
                value={sessionFirm?.Name || "Please select Firm from Header"}
                readOnly
                disabled
              />
            </Col>

            {/* Department */}
            <Col lg={2} md={3}>
              <label className="form-label fw-semibold fs-13" style={{ color: "#495057" }}>Department</label>
              <select
                className="form-select"
                style={{ height: "38px", borderRadius: "6px", fontSize: "13px" }}
                value={selectedDept}
                onChange={(e) => setSelectedDept(e.target.value)}
              >
                <option value="">All Departments</option>
                {filterActiveMasters(deptList, selectedDept).map((d) => (
                  <option key={d.Id} value={d.Id}>{d.Name}</option>
                ))}
              </select>
            </Col>

            {/* Month */}
            <Col lg={2} md={3}>
              <label className="form-label fw-semibold fs-13" style={{ color: "#495057" }}>Month</label>
              <select
                className="form-select"
                style={{ height: "38px", borderRadius: "6px", fontSize: "13px" }}
                value={selectedMonth}
                onChange={(e) => {
                  setSelectedMonth(e.target.value);
                  if (e.target.value) localStorage.setItem("selectedPayrollMonth", e.target.value);
                  else localStorage.removeItem("selectedPayrollMonth");
                }}
              >
                <option value="">-- Month --</option>
                {MONTH_NAMES.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </Col>

            {/* Year */}
            <Col lg={2} md={3}>
              <label className="form-label fw-semibold fs-13" style={{ color: "#495057" }}>Year</label>
              <select
                className="form-select"
                style={{ height: "38px", borderRadius: "6px", fontSize: "13px" }}
                value={selectedYear}
                onChange={(e) => {
                  setSelectedYear(parseInt(e.target.value) || 0);
                  if (e.target.value) localStorage.setItem("selectedPayrollYear", e.target.value);
                  else localStorage.removeItem("selectedPayrollYear");
                }}
              >
                {YEAR_LIST.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </Col>

            {/* Search */}
            <Col lg={2} md={12}>
              <label className="form-label fw-semibold fs-13" style={{ color: "#495057" }}>Search Employee</label>
              <input
                type="text"
                className="form-control"
                style={{ height: "38px", borderRadius: "6px", fontSize: "13px" }}
                placeholder="Name, UAN..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {!sessionFirm?.Id ? (
        <Card className="shadow-sm border-0">
          <Card.Body className="text-center p-5">
            <h5 className="text-warning mb-2 fw-semibold">⚠ Session Setup Required</h5>
            <p className="text-muted mb-0 fs-13">
              Please select a **Firm**, **Month**, and **Year** to load the report.
            </p>
          </Card.Body>
        </Card>
      ) : (
        <Fragment>
          {/* Web View */}
          <div className="no-print">
            <Card className="shadow-sm border-0" style={{ borderRadius: "10px" }}>
              <Card.Header className="d-flex justify-content-between align-items-center bg-white border-bottom py-3 px-4">
                <div>
                  <h5 className="mb-0 text-dark fw-bold fs-16">Wages Slips (Individual Layout)</h5>
                  <small className="text-muted">
                    Total: <strong>{slips.length}</strong>
                  </small>
                </div>
                <div>
                  <Button variant="danger" size="sm" className="me-2" onClick={exportToPDF}>PDF Download</Button>
                  <Button variant="primary" size="sm" onClick={() => window.print()}>Print Slips</Button>
                </div>
              </Card.Header>

              <Card.Body className="p-4 bg-light">
                {isLoading ? (
                  <div className="text-center p-5">
                    <div className="spinner-border text-primary" role="status" />
                    <div className="text-muted mt-2 fs-13">Loading Slips...</div>
                  </div>
                ) : slips.length === 0 ? (
                  <div className="text-center text-muted p-5">No slips available.</div>
                ) : (
                  <Row className="g-4 justify-content-center">
                    {slips.map(slip => (
                      <Col key={slip.empNo} xl={8} lg={10} md={12}>
                        <Card className="border shadow bg-white p-4 fs-12" style={{ borderRadius: "8px" }}>
                          <div className="text-center mb-3">
                            <h5 className="fw-bold mb-1 text-dark fs-15">{sessionFirm?.Name}</h5>
                            <small className="text-muted fs-11">{getFirmAddress()}</small>
                            <div className="fw-bold mt-2 text-secondary fs-12 border-bottom pb-2">
                              WAGES/SALARY SLIP FOR THE MONTH OF {selectedMonth?.toUpperCase()} {selectedYear}
                            </div>
                          </div>

                          <Row className="mb-3 g-2 border-bottom pb-2">
                            <Col md={4}><strong>Emp No :</strong> {slip.empNo}</Col>
                            <Col md={4}><strong>Name :</strong> {slip.name}</Col>
                            <Col md={4}><strong>Father Name :</strong> {slip.fatherName}</Col>
                            <Col md={4}><strong>Department :</strong> {slip.department}</Col>
                            <Col md={4}><strong>Designation :</strong> {slip.designation}</Col>
                            <Col md={4}><strong>Join Date :</strong> {slip.joinDate}</Col>
                            <Col md={6}><strong>PF No / UAN :</strong> {getFirmPfNo()} / {slip.uan}</Col>
                            <Col md={6}><strong>E.S.I. No. :</strong> {slip.esiNo}</Col>
                          </Row>

                          <Table bordered size="sm" className="text-center slip-preview-table mb-3">
                            <thead className="bg-light">
                              <tr>
                                <th>DAYS DETAIL</th>
                                <th>SALARY STRUCTURE</th>
                                <th>EARNINGS</th>
                                <th>DEDUCTION</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td className="text-start">
                                  WD: {slip.wd.toFixed(2)}<br />
                                  WH: {slip.wh.toFixed(2)}<br />
                                  LW: {slip.lw.toFixed(2)}<br />
                                  FH: {slip.fh.toFixed(2)}<br />
                                  CL: {slip.cl.toFixed(2)}<br />
                                  ML: {slip.ml.toFixed(2)}<br />
                                  <strong>PD: {slip.pd.toFixed(2)}</strong>
                                </td>
                                <td className="text-start">
                                  BASIC: {slip.basicRate.toFixed(2)}<br />
                                  HRA: {slip.hraRate.toFixed(2)}<br />
                                  CON: {slip.conRate.toFixed(2)}<br />
                                  <strong>TOTAL: {slip.totalRate.toFixed(2)}</strong>
                                </td>
                                <td className="text-start">
                                  Wages: {slip.earnedBasic.toFixed(2)}<br />
                                  Arrear: {slip.arrear.toFixed(2)}<br />
                                  HRA: {slip.earnedHRA.toFixed(2)}<br />
                                  CON: {slip.earnedConveyance.toFixed(2)}<br />
                                  Adv. Bonus: {slip.bonus.toFixed(2)}<br />
                                  OT Amt: {slip.otAmount.toFixed(2)}<br />
                                  <strong>PAID: {slip.grossWages.toFixed(2)}</strong>
                                </td>
                                <td className="text-start">
                                  TDS: {slip.tds.toFixed(2)}<br />
                                  Recovery: {slip.recovery.toFixed(2)}<br />
                                  ESI: {slip.esi.toFixed(2)}<br />
                                  Emplr ESI: {slip.emplrEsi.toFixed(2)}<br />
                                  PF: {slip.pf.toFixed(2)}<br />
                                  EPF: {slip.epf.toFixed(2)}<br />
                                  Pen: {slip.pension.toFixed(2)}
                                </td>
                              </tr>
                              <tr className="bg-light-subtle">
                                <td className="text-start">OT Hours: {slip.otHours}</td>
                                <td></td>
                                <td><strong>GROSS: ₹{slip.grossWages}</strong></td>
                                <td className="text-start">
                                  <strong>TOTAL DED: ₹{slip.totalDed}</strong><br />
                                  <strong className="text-success">NET PAY: ₹{slip.netPay}</strong>
                                </td>
                              </tr>
                            </tbody>
                          </Table>

                          <div className="d-flex justify-content-between pt-3 mt-2 border-top fw-bold text-muted">
                            <div>EMPLOYER SIGNATURE</div>
                            <div>EMPLOYEE SIGNATURE</div>
                          </div>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                )}
              </Card.Body>
            </Card>
          </div>

          {/* Print Layout */}
          <div id="print-wages-slips-individual" className="d-none">
            {slips.map(slip => (
              <div key={slip.empNo} className="print-individual-slip-page">
                <div style={{ textAlign: "center", fontWeight: "bold", fontSize: "14px" }}>
                  {sessionFirm?.Name?.toUpperCase()}
                </div>
                <div style={{ textAlign: "center", fontSize: "10px", marginTop: "2px" }}>
                  {getFirmAddress()}
                </div>
                <div style={{ textAlign: "center", fontWeight: "bold", fontSize: "11px", margin: "8px 0" }}>
                  WAGES/SALARY SLIP FOR THE MONTH OF {selectedMonth?.toUpperCase()} {selectedYear}
                </div>

                <table style={{ width: "100%", fontSize: "10px", marginBottom: "8px", borderCollapse: "collapse" }}>
                  <tbody>
                    <tr>
                      <td style={{ padding: "4px 0" }}><strong>Emp No:</strong> {slip.empNo}</td>
                      <td><strong>NAME:</strong> {slip.name}</td>
                      <td><strong>FATHER/HUSBAND'S:</strong> {slip.fatherName}</td>
                    </tr>
                    <tr>
                      <td style={{ padding: "4px 0" }}><strong>Department:</strong> {slip.department}</td>
                      <td colSpan={2}><strong>DESIGNATION:</strong> {slip.designation}</td>
                    </tr>
                    <tr>
                      <td style={{ padding: "4px 0" }}><strong>Join Date:</strong> {slip.joinDate}</td>
                      <td><strong>PF No / UAN:</strong> {getFirmPfNo()} / {slip.uan}</td>
                      <td><strong>E.S.I. No.:</strong> {slip.esiNo}</td>
                    </tr>
                    <tr>
                      <td style={{ padding: "4px 0" }}><strong>Bank Name:</strong> {slip.bankName}</td>
                      <td colSpan={2}><strong>Bank A/c No.:</strong> {slip.bankAcNo}</td>
                    </tr>
                  </tbody>
                </table>

                <table className="slip-grid-table">
                  <thead>
                    <tr>
                      <th style={{ width: "25%" }}>DAYS DETAIL</th>
                      <th style={{ width: "25%" }}>SALARY STRUCTURE</th>
                      <th style={{ width: "25%" }}>EARNINGS</th>
                      <th style={{ width: "25%" }}>DEDUCTION</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        WD: {slip.wd.toFixed(2)}<br />
                        WH: {slip.wh.toFixed(2)}<br />
                        LW: {slip.lw.toFixed(2)}<br />
                        FH: {slip.fh.toFixed(2)}<br />
                        CL: {slip.cl.toFixed(2)}<br />
                        ML: {slip.ml.toFixed(2)}<br />
                        <strong>PD: {slip.pd.toFixed(2)}</strong>
                      </td>
                      <td>
                        BASIC: {slip.basicRate.toFixed(2)}<br />
                        HRA: {slip.hraRate.toFixed(2)}<br />
                        CON: {slip.conRate.toFixed(2)}<br />
                        <strong>TOTAL: {slip.totalRate.toFixed(2)}</strong>
                      </td>
                      <td>
                        Wages: {slip.earnedBasic.toFixed(2)}<br />
                        Arrear: {slip.arrear.toFixed(2)}<br />
                        HRA: {slip.earnedHRA.toFixed(2)}<br />
                        CON: {slip.earnedConveyance.toFixed(2)}<br />
                        Adv. Bonus: {slip.bonus.toFixed(2)}<br />
                        OT Amount: {slip.otAmount.toFixed(2)}<br />
                        <strong>PAID: {slip.grossWages.toFixed(2)}</strong>
                      </td>
                      <td>
                        TDS: {slip.tds.toFixed(2)}<br />
                        Recovery: {slip.recovery.toFixed(2)}<br />
                        ESI: {slip.esi.toFixed(2)}<br />
                        Emplr ESI: {slip.emplrEsi.toFixed(2)}<br />
                        PF: {slip.pf.toFixed(2)}<br />
                        EPF: {slip.epf.toFixed(2)}<br />
                        Pen: {slip.pension.toFixed(2)}
                      </td>
                    </tr>
                    <tr style={{ background: "#f9f9f9", fontWeight: "bold" }}>
                      <td>OT Hours: {slip.otHours.toFixed(2)}</td>
                      <td></td>
                      <td>GROSS WAGES: ₹{slip.grossWages}</td>
                      <td>
                        TOTAL DED: ₹{slip.totalDed}<br />
                        NET PAY: ₹{slip.netPay}
                      </td>
                    </tr>
                  </tbody>
                </table>

                <div style={{ display: "flex", justifyContent: "space-between", marginTop: "30px", fontSize: "10px", fontWeight: "bold" }}>
                  <div>EMPLOYER'S SIGNATURE</div>
                  <div>EMPLOYEE'S SIGNATURE</div>
                </div>
              </div>
            ))}
          </div>
        </Fragment>
      )}
    </Fragment>
  );
};

export default WagesSlipIndividual;
